import CloseIcon from "@mui/icons-material/Close";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { NegotiationPaymentMethod } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { IAgreementInstallment, SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";
import { PAYMENT_METHOD_OPTIONS } from "../../../creditRecovery/constants/paymentMethodOptions";

interface Props {
	open: boolean;
	agreementId: string;
	installment: IAgreementInstallment;
	onClose: () => void;
	onConfirm: () => void;
}

const ordinal = (count: number): string => {
	const via = count + 1;
	return `${via}ª via`;
};

export const ModalReissueInstallment = ({ open, agreementId, installment, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [newDueDate, setNewDueDate] = useState("");
	const [paymentMethod, setPaymentMethod] = useState<NegotiationPaymentMethod>("boleto");
	const [note, setNote] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			setNewDueDate(format(new Date(), "yyyy-MM-dd"));
			setPaymentMethod("boleto");
			setNote("");
		}
	}, [open, installment.number]);

	const handleConfirm = async () => {
		if (!newDueDate) return;

		setIsSubmitting(true);
		const result = await trackAsync(
			"agreement.reissue_installment",
			() =>
				SinisterAgreementService.reissueInstallment(agreementId, installment.number, {
					newDueDate,
					paymentMethod,
					note: note || undefined,
				}),
			{ agreementId }
		);
		setIsSubmitting(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		toastHandler.present({
			type: "success",
			messageOrContent: `Nova via da parcela ${installment.number} gerada com sucesso.`,
			position: "top-right",
		});
		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Gerar Nova Via
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Alert severity="info" sx={{ py: 0.5 }}>
							Parcela {installment.number} — {formatScaleValueBR(String(installment.value))} — vencimento atual em{" "}
							{formatDateFnsRemoveTimezone(installment.dueDate) || "-"}. Isso irá gerar a {ordinal(installment.reissueCount ?? 0)}.
						</Alert>

						<TextField
							select
							fullWidth
							size="small"
							label="Forma de pagamento"
							value={paymentMethod}
							onChange={(e) => setPaymentMethod(e.target.value as NegotiationPaymentMethod)}
						>
							{PAYMENT_METHOD_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>

						<TextField
							type="date"
							fullWidth
							size="small"
							label="Novo vencimento"
							value={newDueDate}
							onChange={(e) => setNewDueDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>

						<TextField
							fullWidth
							size="small"
							multiline
							minRows={2}
							label="Observação (opcional)"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Ex: cliente perdeu o boleto anterior e solicitou novo Pix."
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Cancelar</Button>
					<Button variant="contained" disabled={isSubmitting || !newDueDate} onClick={handleConfirm}>
						Gerar Nova Via
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
